import { useContext, useEffect, useState } from "react";
import { Pagination } from "./Pagination";
import { Link } from "react-router-dom";
import { ModalPrompt } from "./ModalPrompt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { default as axios } from "../axiosConfig";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Box,
  Select,
  MenuItem
} from "@mui/material";
import {
  API_AddManualCandidate,
  API_GetAllDistinctSkills,
  API_GetCandidates,
} from "../Assets/ApiUrlContainer";
import UserContext from "../Contexts/UserContext";

const CandidatesListPage = () => {
  const currentUser = useContext(UserContext);

  const [candidates, setCandidates] = useState([]);
  const [filterableCandidates, setFilterableCandidates] = useState([]);
  const [paginatedCandidates, setPaginatedCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [listDisplayStartingIndex, setListDisplayStartingIndex] = useState(0);
  const [listDisplayLimit, setListDisplayLimit] = useState(10);
  const [triggerPageReset, setTriggerPageReset] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [locationFilter, setLocationFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState(""); // "asc", "desc", or ""

  const [searchModalToggleState, setSearchModalToggleState] = useState(false);
  const [searchSkills, setSearchSkills] = useState([]);
  const [searchSkillsModal, setSearchSkillsModal] = useState([]);
  const [skillEntryText, setSkillEntryText] = useState("");
  const [allSkills, setAllSkills] = useState([]);
  const [skillsAvailableToSelect, setSkillsAvailableToSelect] = useState([]);

  const [addCandidateModalToggleState, setAddCandidateModalToggleState] = useState(false);
  const [newCandidateForm, setNewCandidateForm] = useState({
    Email: "",
    Name: "",
    Resume: null,
    CandidateType: "",
    OpenToWork: true,
    ValidWorkPermit: true,
    Active: true,
    Phone: "",
    Summary: "",
    Location: "",
    LinkedIn: "",
    Website: "",
    Experience: "",
    Availability: "",
    CandidateProfileBriefing: null,
    isValid: false
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [peopleProfileFile, setPeopleProfileFile] = useState(null);
  const FILE_MAX_CHAR_DISPLAY_LIMIT = 25;

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const response = await axios.get(API_GetCandidates);
        setCandidates(response.data);
        setFilterableCandidates(filterCandidatesByDropdownFilters(response.data));
        await getAllSkills();

        setLoading(false);
      } catch (error) {
        console.error("Error fetching candidates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Search candidates via API when searchTerm changes
  useEffect(() => {
    const fetchCandidatesAndFilter = async () => {
      if (searchTerm.trim() === "") {
        let statusFilteredCandidates = filterCandidatesByDropdownFilters();

        if (searchSkillsModal.length) {
          filterCandidatesBySkillsSearch(statusFilteredCandidates);
        } else {
          setFilterableCandidates(statusFilteredCandidates);
        }

      } else {
        try {
          let statusFilteredCandidates = filterCandidatesByDropdownFilters();
          let nameAndStatusFilteredCandidates = statusFilteredCandidates.filter(candidate => candidate.Name.toLowerCase().includes(searchTerm.trim().toLowerCase()));

          if (searchSkillsModal.length) {
            filterCandidatesBySkillsSearch(nameAndStatusFilteredCandidates);
          } else {
            setFilterableCandidates(nameAndStatusFilteredCandidates);
          }

        } catch (error) {
          console.error("Error searching candidates:", error);
        }
      }

    };

    fetchCandidatesAndFilter();
  }, [searchTerm, statusFilter, locationFilter, dateFilter]);

  useEffect(() => {
    if (filterableCandidates.length > 0) {
      setPaginatedCandidates(getPaginatedCandidates())
    }
  }, [listDisplayStartingIndex, listDisplayLimit, filterableCandidates])

  const getAllSkills = async () => {
    let allDistinctSkills = await axios.get(API_GetAllDistinctSkills);
    let sortedSkills = allDistinctSkills.data.sort((a, b) => a.Name.localeCompare(b.Name));

    setAllSkills(sortedSkills);
    setSkillsAvailableToSelect(sortedSkills);
  }

  const handlePageChange = (newPositionIndex) => setListDisplayStartingIndex(newPositionIndex);
  const handlePageListLimitChange = (newListDisplayLimit) => setListDisplayLimit(newListDisplayLimit);

  const getPaginatedCandidates = () => filterableCandidates.slice(listDisplayStartingIndex, listDisplayLimit);

  const handleSearchModalClose = (isResetFilter = false) => {
    setSearchModalToggleState(false);
    setSkillEntryText("");

    if (!isResetFilter) {
      setSearchSkillsModal(searchSkills);
      setSkillsAvailableToSelect(allSkills.filter((skill) => !searchSkills.join(", ").toLowerCase().includes(skill.Name.toLowerCase())));
    } else {
      setSearchSkillsModal([]);
      setSearchSkills([]);
      setSkillsAvailableToSelect(allSkills);
      setFilterableCandidates(filterCandidatesByDropdownFilters());
    }
  };

  const handleSkillTextEntry = (event) => {
    setSkillEntryText(event.target.value);
  };

  const removeSkillFromList = (skillName) => {
    let relevantSkills = [...searchSkillsModal];
    let indexSkill = relevantSkills.findIndex(
      (skill) => skill.toLowerCase() === skillName.trim().toLowerCase()
    );

    let newSkillsList = [
      ...relevantSkills.slice(0, indexSkill),
      ...relevantSkills.slice(indexSkill + 1, relevantSkills.length),
    ];

    setSearchSkillsModal(newSkillsList);
    setSkillsAvailableToSelect(allSkills.filter((skill) => !newSkillsList.join(", ").toLowerCase().includes(skill.Name.toLowerCase())));
  };

  const addSkillToList = () => {
    let skills = [...searchSkillsModal];
    if (!skills.some((skill) => skill.toLowerCase() === skillEntryText.trim().toLowerCase())) {
      skills.push(skillEntryText.trim());

      setSearchSkillsModal(skills);
      setSkillsAvailableToSelect(allSkills.filter((skill) => !skills.join(", ").toLowerCase().includes(skill.Name.toLowerCase())));
    }
  };

  const handleSkillEntryEnterKey = (event) => {
    if (event.key === "Enter") {
      addSkillToList();
      setSkillEntryText("");
    }
  };

  const openSearchBySkillsModal = () => {
    setSearchModalToggleState(true);
  }

  const handleFilterClearWithReset = () => {
    handleSearchModalClose(true);
  }

  const openAddCandidateModal = () => {
    setAddCandidateModalToggleState(true);
  }

  const updateNewCandidateDetails = (fieldName, fieldValue) => {
    let newCandidateFormUpdate = {
      ...newCandidateForm,
      [fieldName]: fieldValue
    };

    newCandidateFormUpdate.isValid = (
      newCandidateFormUpdate.Name !== "" &&
      newCandidateFormUpdate.Email !== "" &&
      newCandidateFormUpdate.Resume !== null &&
      newCandidateFormUpdate.Location !== "" &&
      newCandidateFormUpdate.LinkedIn !== "" &&
      newCandidateFormUpdate.CandidateType !== "" &&
      newCandidateFormUpdate.Experience !== "" &&
      newCandidateFormUpdate.Availability !== ""
    );

    setNewCandidateForm(newCandidateFormUpdate);
  }

  const handleAddNewCandidate = async () => {
    setLoading(true);

    const formData = new FormData();
    formData.append("Name", newCandidateForm.Name);
    formData.append("Email", newCandidateForm.Email);
    formData.append("CandidateType", newCandidateForm.CandidateType);
    formData.append("OpenToWork", newCandidateForm.OpenToWork);
    formData.append("ValidWorkPermit", newCandidateForm.ValidWorkPermit);
    formData.append("Active", newCandidateForm.Active);
    formData.append("Phone", newCandidateForm.Phone);
    formData.append("Summary", newCandidateForm.Summary);
    formData.append("Location", newCandidateForm.Location);
    formData.append("LinkedIn", newCandidateForm.LinkedIn);
    formData.append("Website", newCandidateForm.Website);
    formData.append("Experience", newCandidateForm.Experience);
    formData.append("Availability", newCandidateForm.Availability);

    if (resumeFile) formData.append("Resume", resumeFile);
    if (peopleProfileFile) formData.append('CandidateProfileBriefing', peopleProfileFile);

    const response = await axios.post(API_AddManualCandidate, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    setCandidates(response.data);
    setFilterableCandidates(filterCandidatesByDropdownFilters(response.data));

    await getAllSkills();
    setLoading(false);
    handleCloseCandidateModal();
  }

  const handleCloseCandidateModal = () => {
    setAddCandidateModalToggleState(false);
    setPeopleProfileFile(null);
    setResumeFile(null);

    // reset candidateDetails
    setNewCandidateForm({
      Email: "",
      Name: "",
      Resume: "",
      CandidateType: "",
      OpenToWork: true,
      ValidWorkPermit: true,
      Active: true,
      Phone: "",
      Summary: "",
      Location: "",
      LinkedIn: "",
      Website: "",
      Experience: "",
      Availability: "",
      CandidateProfileBriefing: "",
      isValid: false
    });
  }

  const filterCandidatesByDropdownFilters = (candidatesToFilter = candidates) => {
    return candidatesToFilter.filter((candidate) => {
      if (statusFilter !== "All" && candidate.State !== statusFilter) return false;
      if (locationFilter !== "All" && candidate.Location !== locationFilter) return false;
      return true;

    }).sort((a, b) => {
      if (dateFilter === "asc") return new Date(a.CreatedAt) - new Date(b.CreatedAt);
      else if (dateFilter === "desc") return new Date(b.CreatedAt) - new Date(a.CreatedAt);
      else return 0;
    });
  }

  const filterCandidatesBySkillsSearch = (candidatesList = candidates) => {
    let searchSkills = searchSkillsModal;
    setSearchSkills(searchSkillsModal);
    setSearchModalToggleState(false);

    let candidatesWithSkills = [];

    for (let candidate of candidatesList) {
      let skillsList = candidate.SkillsList?.toLowerCase().split(",").map(str => str.trim());
      for (let searchSkill of searchSkills) {
        if (skillsList && skillsList.includes(searchSkill.toLowerCase().trim())) {

          // if there is no entry for a matching candidate already in the list, then create one
          if (!candidatesWithSkills.some(candidateWithSkill => candidateWithSkill.candidate.Id === candidate.Id)) {
            candidatesWithSkills.push({
              score: 1,
              candidate
            });

          } else {
            // we have a matching candidate so just increase the score
            let indexCandidateWithSkill = candidatesWithSkills.findIndex(candidateWithSkill => candidateWithSkill.candidate.Id === candidate.Id);
            candidatesWithSkills[indexCandidateWithSkill].score++;
          }
        }
      }
    }

    let sortedFilterCandidates = candidatesWithSkills.sort((a, b) => b.score - a.score);
    setFilterableCandidates(sortedFilterCandidates.map(sortedCandidates => sortedCandidates.candidate));
  }

  const handleFileChange = (event, isResumeFile) => {
    const file = event.target.files[0];

    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Only PDF files are allowed.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size exceeds 5MB limit.');
        return;
      }

      isResumeFile ? setResumeFile(file) : setPeopleProfileFile(file);
      updateNewCandidateDetails(isResumeFile ? "Resume" : "CandidateProfileBriefing", file);
    }
  };

  const getFileUploadFileName = (isResumeFile) => {
    let text = "Choose File";
    if (isResumeFile && resumeFile !== null) text = resumeFile.name;
    if (!isResumeFile && peopleProfileFile !== null) text = peopleProfileFile.name;

    if (text !== 'Choose File') {
      let needsCondensing = text.length > FILE_MAX_CHAR_DISPLAY_LIMIT;
      if (needsCondensing) {
        text = `${text.slice(0, FILE_MAX_CHAR_DISPLAY_LIMIT)}...`;
      }
    }

    return text;
  }

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <>
      <div className="tgs-content">
        <Box sx={{ padding: "16px" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ marginBottom: 2 }}
          >
            <Typography variant="h4" gutterBottom>
              Applicants
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{
              marginBottom: 2,
              flexWrap: "wrap",
              rowGap: 2,
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: 300,
                backgroundColor: "#f5f5f5",
              }}
            />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              variant="outlined"
              sx={{ width: 150, backgroundColor: "#f5f5f5" }}
            >
              <MenuItem value="All">All Statuses</MenuItem>
              {[...new Set(candidates.map((c) => c.State))]
                .filter(Boolean)
                .map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
            </Select>

            <Select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              variant="outlined"
              sx={{ width: 180, backgroundColor: "#f5f5f5" }}
            >
              <MenuItem value="All">All Locations</MenuItem>
              {[...new Set(candidates.map((c) => c.Location))]
                .filter(Boolean)
                .map((location) => (
                  <MenuItem key={location} value={location}>
                    {location}
                  </MenuItem>
                ))}
            </Select>

            <Select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              variant="outlined"
              sx={{ width: 180, backgroundColor: "#f5f5f5" }}
              displayEmpty
            >
              <MenuItem value="">Sort by Date</MenuItem>
              <MenuItem value="desc">Newest First</MenuItem>
              <MenuItem value="asc">Oldest First</MenuItem>
            </Select>

            <div className="addCandidateManuallyBtn">
              <button onClick={openAddCandidateModal}>Add Candidate Manually</button>
            </div>

            <div className="searchBySkillsBtn">
              <button onClick={openSearchBySkillsModal}>Search By Skills</button>
            </div>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>State</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCandidates.map((candidate) => (
                  <TableRow key={candidate.Id}>
                    <TableCell>
                      <Link to={`/applicants/${encodeURIComponent(candidate.Id)}`}>
                        {candidate.Name}
                      </Link>
                    </TableCell>
                    <TableCell>{candidate.Email}</TableCell>
                    <TableCell>{candidate.Location || "N/A"}</TableCell>
                    <TableCell>{candidate.State || "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Pagination
            totalNumItems={filterableCandidates.length}
            onPageChangeNewPosition={handlePageChange}
            onRowsChangeNewDisplayLimit={handlePageListLimitChange}
            triggerPageReset={triggerPageReset}
            onTriggerReset={setTriggerPageReset}
          />
        </Box>
      </div>

      <ModalPrompt
        onModalClose={handleCloseCandidateModal}
        modalToggleState={addCandidateModalToggleState}
        modalTitle="Manually Add A Candidate"
        modalContent={
          <>
            <div className="addCandidateManuallyModal">
              <div className="addCandidateManuallyMsg">
                Please fill in the candidate details manually.
              </div>

              <div className="candidateFormContainer">
                <div className="formRow">
                  <div className="label required">Full Name</div>
                  <div className="entryField">
                    <textarea
                      value={newCandidateForm.Name}
                      onChange={(e) => updateNewCandidateDetails("Name", e.target.value)}
                    ></textarea>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label required">Email Address</div>
                  <div className="entryField">
                    <textarea
                      value={newCandidateForm.Email}
                      onChange={(e) => updateNewCandidateDetails("Email", e.target.value)}
                    ></textarea>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label">Phone Number</div>
                  <div className="entryField">
                    <textarea
                      value={newCandidateForm.Phone}
                      onChange={(e) => updateNewCandidateDetails("Phone", e.target.value)}
                    ></textarea>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label required">Years of Experience</div>
                  <div className="entryField">
                    <select
                      value={newCandidateForm.Experience}
                      onChange={(e) => updateNewCandidateDetails("Experience", e.target.value)}>
                      <option value=''>--Select--</option>
                      <option value='>5 years'>&gt;5 years</option>
                      <option value='2-5 years'>2-5 years</option>
                      <option value='<2 years'>&lt;2 years</option>
                      <option value='Student'>student</option>
                    </select>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label required">Time Availability</div>
                  <div className="entryField">
                    <select
                      value={newCandidateForm.Availability}
                      onChange={(e) => updateNewCandidateDetails("Availability", e.target.value)}>
                      <option value=''>--Select--</option>
                      <option value='Full-time'>Full-time</option>
                      <option value='Part-time'>Part-time</option>
                      <option value='Gig'>Gig</option>
                    </select>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label required">Candidate Type</div>
                  <div className="entryField">
                    <select
                      value={newCandidateForm.CandidateType}
                      onChange={(e) => updateNewCandidateDetails("CandidateType", e.target.value)}>
                      <option value=''>--Select--</option>
                      <option value='New'>New</option>
                      <option value='Rejected'>Rejected</option>
                      <option value='Resubmitted'>Resubmitted</option>
                      <option value='TGS Verified'>TGS Verified</option>
                      <option value='TGS Verified A'>TGS Verified A</option>
                      <option value='TGS Employed'>TGS Employed</option>
                    </select>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label">Valid Work Permit</div>
                  <div className="entryField">
                    <select
                      value={newCandidateForm.ValidWorkPermit}
                      onChange={(e) => updateNewCandidateDetails("ValidWorkPermit", e.target.value)}>
                      <option value=''>--Select--</option>
                      <option value={false}>No</option>
                      <option value={true}>Yes</option>
                    </select>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label">Open To Work</div>
                  <div className="entryField">
                    <select
                      value={newCandidateForm.OpenToWork}
                      onChange={(e) => updateNewCandidateDetails("OpenToWork", e.target.value)}>
                      <option value=''>--Select--</option>
                      <option value={false}>No</option>
                      <option value={true}>Yes</option>
                    </select>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label required">Location</div>
                  <div className="entryField">
                    <textarea
                      value={newCandidateForm.Location}
                      onChange={(e) => updateNewCandidateDetails("Location", e.target.value)}
                    ></textarea>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label required">LinkedIn Profile</div>
                  <div className="entryField">
                    <textarea
                      value={newCandidateForm.LinkedIn}
                      onChange={(e) => updateNewCandidateDetails("LinkedIn", e.target.value)}
                    ></textarea>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label">Website</div>
                  <div className="entryField">
                    <textarea
                      value={newCandidateForm.Website}
                      onChange={(e) => updateNewCandidateDetails("Website", e.target.value)}
                    ></textarea>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label">People Profile PDF</div>
                  <div className="entryField">
                    {/* <textarea
                      value={newCandidateForm.CandidateProfileBriefing}
                      onChange={(e) => updateNewCandidateDetails("CandidateProfileBriefing", e.target.value)}
                    ></textarea> */}
                    <label className="customFileUpload">
                      <input
                        id={`file-upload-peopleProfile`}
                        style={{ display: 'none' }}
                        type='file'
                        accept='application/pdf'
                        onChange={(e) => handleFileChange(e, false)}
                        className='fileInput'
                      />
                      {getFileUploadFileName(false)}
                    </label>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label required">Resume PDF</div>
                  <div className="entryField">
                    {/* <textarea
                      value={newCandidateForm.Resume}
                      onChange={(e) => updateNewCandidateDetails("Resume", e.target.value)}
                    ></textarea> */}

                    <label className="customFileUpload">
                      <input
                        id={`file-upload-resume`}
                        style={{ display: 'none' }}
                        type='file'
                        accept='application/pdf'
                        onChange={(e) => handleFileChange(e, true)}
                        className='fileInput'
                      />
                      {getFileUploadFileName(true)}
                    </label>
                  </div>
                </div>

                <div className="formRow">
                  <div className="label">Summary</div>
                  <div className="entryField">
                    <textarea
                      className="summaryField"
                      value={newCandidateForm.Summary}
                      onChange={(e) => updateNewCandidateDetails("Summary", e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </div>

              <div className="modalBtns">
                <button className="cancelBtn" onClick={handleCloseCandidateModal}>
                  CANCEL
                </button>

                <button
                  className="confirmBtn"
                  onClick={handleAddNewCandidate}
                  disabled={!newCandidateForm.isValid}
                >
                  CONFIRM
                </button>
              </div>
            </div>
          </>
        }
      />

      <ModalPrompt
        onModalClose={() => handleSearchModalClose()}
        modalToggleState={searchModalToggleState}
        modalTitle="Select Skills To Search By"
        modalContent={
          <>
            <div className="searchCandidateBySkillsModal">
              <div className="searchCandidateBySkillsMsg">
                Enter in the skills you would like to search for a candidate by.
              </div>

              <div className="skillsFilter">
                <span>Skills</span>
                <div className="skillEntry">
                  {searchSkillsModal.map((skill) => {
                    return (
                      <div className="skillEntryBubble" key={skill}>
                        <span>{skill}</span>
                        <FontAwesomeIcon
                          icon={faCircleXmark}
                          onClick={() => removeSkillFromList(skill)} />
                      </div>
                    );
                  })}
                </div>
                <input
                  type="text"
                  list="skillsList"
                  value={skillEntryText}
                  onInput={handleSkillTextEntry}
                  onKeyDown={handleSkillEntryEnterKey}
                />
                <datalist id="skillsList">
                  {skillsAvailableToSelect.map((skill) => (
                    <option key={skill.Id}>{skill.Name}</option>
                  ))}
                </datalist>
              </div>

              <div className="modalBtns">
                <button className="resetBtn" onClick={handleFilterClearWithReset}>
                  RESET FILTER
                </button>

                <button className="cancelBtn" onClick={() => handleSearchModalClose()}>
                  CANCEL
                </button>

                <button
                  className="confirmBtn"
                  onClick={() => filterCandidatesBySkillsSearch()}
                  disabled={!searchSkillsModal.length}
                >
                  CONFIRM
                </button>
              </div>
            </div>
          </>
        }
      />
    </>
  );
};

export default CandidatesListPage;
